import React, { useState } from "react";
import { X, Building2, User, Briefcase, GraduationCap, ArrowRight, Mail, Phone, MessageSquare, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";
import { collection, addDoc, getFirestore } from "firebase/firestore";
import { firebaseApp } from "../firebase";

export default function BookDemoModal({ isOpen, onClose }) {
  const [userType, setUserType] = useState(null);
  const [individualType, setIndividualType] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    companyName: "",
    companyEmail: "",
    phone: "",
    currentRole: "",
    experience: "",
    education: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    setIndividualType(null);
    // Reset form data when switching user types
    setFormData({
      name: "",
      email: "",
      companyName: "",
      companyEmail: "",
      phone: "",
      currentRole: "",
      experience: "",
      education: "",
      message: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation based on user type
    if (userType === "company") {
      if (!formData.name || !formData.companyName || !formData.companyEmail || !formData.phone) {
        toast.error("Please fill in all required fields");
        return;
      }
    } else if (userType === "individual") {
      if (!formData.name || !formData.email || !individualType) {
        toast.error("Please fill in all required fields");
        return;
      }
    } else {
      toast.error("Please select your user type");
      return;
    }

    try {
      const db = getFirestore(firebaseApp);
      const formDataWithTimestamp = {
        userType,
        individualType: userType === "individual" ? individualType : null,
        ...formData,
        submittedAt: new Date(),
        timestamp: new Date().toISOString(),
      };

      await addDoc(collection(db, "demoRequests"), formDataWithTimestamp);

      toast.success("Demo booking request submitted successfully!");

      // Reset form
      setUserType(null);
      setIndividualType(null);
      setFormData({
        name: "",
        email: "",
        companyName: "",
        companyEmail: "",
        phone: "",
        currentRole: "",
        experience: "",
        education: "",
        message: "",
      });

      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit request. Please try again.");
    }
  };

  const handleClose = () => {
    setUserType(null);
    setIndividualType(null);
    setFormData({
      name: "",
      email: "",
      companyName: "",
      companyEmail: "",
      phone: "",
      currentRole: "",
      experience: "",
      education: "",
      message: "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center p-6 sm:p-8 border-b border-gray-100 sticky top-0 bg-gradient-to-r from-white to-gray-50 z-10">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-red-500 to-primary bg-clip-text text-transparent">
              Book Your Demo
            </h2>
            <p className="text-sm text-gray-500 mt-1">Join VetriaAI and transform your hiring</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-300 p-2 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
          {/* User Type Selection */}
          {!userType ? (
            <div className="space-y-6">
              <div>
                <label className="block text-base font-semibold text-gray-900 mb-4">
                  Who are you? *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => handleUserTypeSelect("company")}
                    className="group relative overflow-hidden rounded-xl border-2 border-gray-200 p-6 transition-all duration-300 hover:border-primary hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex flex-col items-center justify-center">
                      <div className="mb-3 p-3 bg-gradient-to-br from-primary/10 to-red-500/10 rounded-lg group-hover:from-primary/20 group-hover:to-red-500/20 transition-colors duration-300">
                        <Building2 className="w-7 h-7 text-primary group-hover:text-primary transition-colors" />
                      </div>
                      <span className="font-semibold text-gray-700 text-center group-hover:text-primary transition-colors duration-300 text-sm sm:text-base">
                        Company / HR
                      </span>
                      <span className="text-xs text-gray-400 mt-1">Founder, HR, Recruiter</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUserTypeSelect("individual")}
                    className="group relative overflow-hidden rounded-xl border-2 border-gray-200 p-6 transition-all duration-300 hover:border-primary hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex flex-col items-center justify-center">
                      <div className="mb-3 p-3 bg-gradient-to-br from-primary/10 to-red-500/10 rounded-lg group-hover:from-primary/20 group-hover:to-red-500/20 transition-colors duration-300">
                        <User className="w-7 h-7 text-primary group-hover:text-primary transition-colors" />
                      </div>
                      <span className="font-semibold text-gray-700 text-center group-hover:text-primary transition-colors duration-300 text-sm sm:text-base">
                        Job Seeker
                      </span>
                      <span className="text-xs text-gray-400 mt-1">Student, Experienced, Switcher</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Back Button */}
              <button
                type="button"
                onClick={() => {
                  setUserType(null);
                  setIndividualType(null);
                }}
                className="inline-flex items-center text-sm text-gray-600 hover:text-primary transition-colors duration-300 font-medium group"
              >
                <ArrowRight className="w-4 h-4 mr-2 rotate-180 group-hover:-translate-x-1 transition-transform" />
                Change Selection
              </button>

              {/* Individual Type Selection (if individual selected) */}
              {userType === "individual" && !individualType && (
                <div className="space-y-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                  <label className="block text-base font-semibold text-gray-900">
                    What's your profile? *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setIndividualType("student")}
                      className="group relative overflow-hidden rounded-lg border-2 border-gray-300 p-4 transition-all duration-300 hover:border-primary hover:shadow-md hover:-translate-y-0.5"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex flex-col items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-primary transition-colors text-center">
                          Student
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIndividualType("experienced")}
                      className="group relative overflow-hidden rounded-lg border-2 border-gray-300 p-4 transition-all duration-300 hover:border-primary hover:shadow-md hover:-translate-y-0.5"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex flex-col items-center justify-center">
                        <Briefcase className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-primary transition-colors text-center">
                          Experienced
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIndividualType("career-switcher")}
                      className="group relative overflow-hidden rounded-lg border-2 border-gray-300 p-4 transition-all duration-300 hover:border-primary hover:shadow-md hover:-translate-y-0.5"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex flex-col items-center justify-center">
                        <ArrowRight className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-primary transition-colors text-center">
                          Switcher
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Form Fields */}
              {(userType === "company" || (userType === "individual" && individualType)) && (
                <div className="space-y-5">
                  {/* Company Fields */}
                  {userType === "company" && (
                    <>
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          Your Full Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="John Doe"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="companyName"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          Company Name *
                        </label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="Your Company Inc"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="companyEmail"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          Company Email Address *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="email"
                            id="companyEmail"
                            name="companyEmail"
                            value={formData.companyEmail}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="contact@company.com"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          Phone Number *
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="+91 98765 43210"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Individual Fields */}
                  {userType === "individual" && individualType && (
                    <>
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          Full Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="Jane Doe"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          Email Address *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="jane@example.com"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="+91 98765 43210"
                          />
                        </div>
                      </div>

                      {individualType === "student" && (
                        <div>
                          <label
                            htmlFor="education"
                            className="block text-sm font-semibold text-gray-800 mb-2"
                          >
                            Current Education / Course
                          </label>
                          <input
                            type="text"
                            id="education"
                            name="education"
                            value={formData.education}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                            placeholder="e.g., B.Tech Computer Science, MBA"
                          />
                        </div>
                      )}

                      {(individualType === "experienced" || individualType === "career-switcher") && (
                        <>
                          <div>
                            <label
                              htmlFor="currentRole"
                              className="block text-sm font-semibold text-gray-800 mb-2"
                            >
                              Current Role / Position
                            </label>
                            <input
                              type="text"
                              id="currentRole"
                              name="currentRole"
                              value={formData.currentRole}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400"
                              placeholder="e.g., Software Engineer, Marketing Manager"
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="experience"
                              className="block text-sm font-semibold text-gray-800 mb-2"
                            >
                              Years of Experience
                            </label>
                            <select
                              id="experience"
                              name="experience"
                              value={formData.experience}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400 appearance-none cursor-pointer"
                            >
                              <option value="">Select experience</option>
                              <option value="0-1">0-1 years</option>
                              <option value="2-3">2-3 years</option>
                              <option value="4-5">4-5 years</option>
                              <option value="6-10">6-10 years</option>
                              <option value="10+">10+ years</option>
                            </select>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Common Message Field */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-gray-800 mb-2"
                    >
                      Tell us more (Optional)
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                      <textarea
                        id="message"
                        name="message"
                        rows={3}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white hover:border-gray-400 resize-none"
                        placeholder="Tell us about your specific needs or questions..."
                      />
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-primary to-red-500 text-white rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 font-semibold flex items-center justify-center gap-2 group"
                    >
                      <span>Book Demo</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </form>
      </div>
    </div>
  );
}
